import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Divider,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { AutoCoderFramework, AutoCoderCode, AutoCoderVariable } from '../../../types/gotypes'
import { MATCH_EVIDENCE_COLORS } from '../colorPalette'

interface ManualFrameworkEditorProps {
    frameworks: AutoCoderFramework[]
    onChange: (frameworks: AutoCoderFramework[]) => void
    disabled?: boolean
    showLoopedVariables?: boolean
}

const ManualFrameworkEditor: React.FC<ManualFrameworkEditorProps> = ({
    frameworks,
    onChange,
    disabled = false,
    showLoopedVariables = false,
}) => {
    // Ensure we have at least one framework
    const currentFramework = frameworks[0] || {
        name: 'codes',
        main_variable: { name: 'topic', codes: [] },
        suggestions: [],
        looped_variables: [],
    }

    // JSON editor state
    const [jsonText, setJsonText] = useState('')
    const [jsonError, setJsonError] = useState<string | null>(null)

    // Sync form → JSON when framework changes
    useEffect(() => {
        if (showLoopedVariables) {
            setJsonText(JSON.stringify(currentFramework, null, 2))
        } else {
            // Strip looped_variables from JSON display
            const { looped_variables, ...rest } = currentFramework
            setJsonText(JSON.stringify(rest, null, 2))
        }
        setJsonError(null)
    }, [currentFramework, showLoopedVariables])

    const updateFramework = (updated: AutoCoderFramework) => {
        onChange([updated])
    }

    // Handle JSON text area changes
    const handleJsonChange = (value: string) => {
        setJsonText(value)
        try {
            const parsed = JSON.parse(value) as AutoCoderFramework
            // Basic validation - check required fields exist
            if (parsed.main_variable && Array.isArray(parsed.main_variable.codes)) {
                setJsonError(null)
                updateFramework(parsed)
            } else {
                setJsonError('Invalid framework structure: missing main_variable or codes')
            }
        } catch (e) {
            setJsonError('Invalid JSON syntax')
        }
    }

    const addCode = () => {
        const newCode: AutoCoderCode = {
            code: String(currentFramework.main_variable.codes.length + 1),
            text: '',
            description: '',
        }
        updateFramework({
            ...currentFramework,
            main_variable: {
                ...currentFramework.main_variable,
                codes: [...currentFramework.main_variable.codes, newCode],
            },
        })
    }

    const updateCode = (index: number, field: keyof AutoCoderCode, value: string) => {
        const codes = [...currentFramework.main_variable.codes]
        codes[index] = { ...codes[index], [field]: value }
        updateFramework({
            ...currentFramework,
            main_variable: {
                ...currentFramework.main_variable,
                codes,
            },
        })
    }

    const removeCode = (index: number) => {
        const codes = currentFramework.main_variable.codes.filter((_, i) => i !== index)
        updateFramework({
            ...currentFramework,
            main_variable: {
                ...currentFramework.main_variable,
                codes,
            },
        })
    }

    const addLoopedVariable = () => {
        const newVariable: AutoCoderVariable = {
            name: `variable_${currentFramework.looped_variables.length + 1}`,
            codes: [],
        }
        updateFramework({
            ...currentFramework,
            looped_variables: [...currentFramework.looped_variables, newVariable],
        })
    }

    const updateLoopedVariable = (varIndex: number, field: 'name', value: string) => {
        const loopedVars = [...currentFramework.looped_variables]
        loopedVars[varIndex] = { ...loopedVars[varIndex], [field]: value }
        updateFramework({ ...currentFramework, looped_variables: loopedVars })
    }

    const removeLoopedVariable = (varIndex: number) => {
        const loopedVars = currentFramework.looped_variables.filter((_, i) => i !== varIndex)
        updateFramework({ ...currentFramework, looped_variables: loopedVars })
    }

    const addLoopedVariableCode = (varIndex: number) => {
        const loopedVars = [...currentFramework.looped_variables]
        const newCode: AutoCoderCode = {
            code: String(loopedVars[varIndex].codes.length + 1),
            text: '',
            description: '',
        }
        loopedVars[varIndex] = {
            ...loopedVars[varIndex],
            codes: [...loopedVars[varIndex].codes, newCode],
        }
        updateFramework({ ...currentFramework, looped_variables: loopedVars })
    }

    const updateLoopedVariableCode = (varIndex: number, codeIndex: number, field: keyof AutoCoderCode, value: string) => {
        const loopedVars = [...currentFramework.looped_variables]
        const codes = [...loopedVars[varIndex].codes]
        codes[codeIndex] = { ...codes[codeIndex], [field]: value }
        loopedVars[varIndex] = { ...loopedVars[varIndex], codes }
        updateFramework({ ...currentFramework, looped_variables: loopedVars })
    }

    const removeLoopedVariableCode = (varIndex: number, codeIndex: number) => {
        const loopedVars = [...currentFramework.looped_variables]
        loopedVars[varIndex] = {
            ...loopedVars[varIndex],
            codes: loopedVars[varIndex].codes.filter((_, i) => i !== codeIndex),
        }
        updateFramework({ ...currentFramework, looped_variables: loopedVars })
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Manual Framework Editor
            </Typography>

            <TextField
                fullWidth
                size="small"
                label="Framework Name"
                value={currentFramework.name}
                onChange={(e) => updateFramework({ ...currentFramework, name: e.target.value })}
                disabled={disabled}
                sx={{ mb: 2 }}
            />

            <TextField
                fullWidth
                size="small"
                label="Main Variable Name"
                helperText="Lowercase letters, numbers, and underscores only"
                value={currentFramework.main_variable.name}
                onChange={(e) => updateFramework({
                    ...currentFramework,
                    main_variable: { ...currentFramework.main_variable, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') },
                })}
                disabled={disabled}
                sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Codes
            </Typography>

            {currentFramework.main_variable.codes.map((code, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <Box
                            sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                backgroundColor: MATCH_EVIDENCE_COLORS[index % MATCH_EVIDENCE_COLORS.length],
                                flexShrink: 0,
                            }}
                        />
                        <TextField
                            size="small"
                            label="Code"
                            value={code.code}
                            onChange={(e) => updateCode(index, 'code', e.target.value)}
                            disabled={disabled}
                            sx={{ width: 100 }}
                        />
                        <TextField
                            size="small"
                            label="Label"
                            value={code.text}
                            onChange={(e) => updateCode(index, 'text', e.target.value)}
                            disabled={disabled}
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            onClick={() => removeCode(index)}
                            disabled={disabled}
                            color="error"
                            aria-label={`Delete code ${code.text || code.code}`}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        label="Description (for LLM context)"
                        placeholder="Describe when this code should be applied..."
                        value={code.description || ''}
                        onChange={(e) => updateCode(index, 'description', e.target.value)}
                        disabled={disabled}
                        multiline
                        rows={2}
                    />
                </Paper>
            ))}

            <Button
                startIcon={<AddIcon />}
                onClick={addCode}
                disabled={disabled}
                sx={{ mb: 3 }}
            >
                Add Code
            </Button>

            {showLoopedVariables && (
              <>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Looped Variables (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add looped variables like sentiment or emotion that apply to each main code.
                </Typography>

                <Button
                    startIcon={<AddIcon />}
                    onClick={addLoopedVariable}
                    disabled={disabled}
                    sx={{ mb: 2 }}
                >
                    Add Looped Variable
                </Button>

                {currentFramework.looped_variables.map((variable, varIndex) => (
                    <Paper key={varIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                label="Variable Name"
                                helperText="Lowercase letters, numbers, and underscores only"
                                value={variable.name}
                                onChange={(e) => updateLoopedVariable(varIndex, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                disabled={disabled}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={() => removeLoopedVariable(varIndex)}
                                disabled={disabled}
                                color="error"
                                aria-label={`Remove looped variable ${variable.name || ''}`}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Codes for {variable.name || 'this variable'}
                        </Typography>

                        {variable.codes.map((code, codeIndex) => (
                            <Paper key={codeIndex} variant="outlined" sx={{ p: 2, mb: 1, ml: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 1,
                                            backgroundColor: MATCH_EVIDENCE_COLORS[codeIndex % MATCH_EVIDENCE_COLORS.length],
                                            flexShrink: 0,
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Code"
                                        value={code.code}
                                        onChange={(e) => updateLoopedVariableCode(varIndex, codeIndex, 'code', e.target.value)}
                                        disabled={disabled}
                                        sx={{ width: 100 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Label"
                                        value={code.text}
                                        onChange={(e) => updateLoopedVariableCode(varIndex, codeIndex, 'text', e.target.value)}
                                        disabled={disabled}
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton
                                        onClick={() => removeLoopedVariableCode(varIndex, codeIndex)}
                                        disabled={disabled}
                                        color="error"
                                        aria-label={`Delete code ${code.text || code.code}`}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Description (for LLM context)"
                                    placeholder="Describe when this code should be applied..."
                                    value={code.description || ''}
                                    onChange={(e) => updateLoopedVariableCode(varIndex, codeIndex, 'description', e.target.value)}
                                    disabled={disabled}
                                    multiline
                                    rows={2}
                                />
                            </Paper>
                        ))}

                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => addLoopedVariableCode(varIndex)}
                            disabled={disabled}
                            size="small"
                            sx={{ ml: 2 }}
                        >
                            Add Code
                        </Button>
                    </Paper>
                ))}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                JSON Editor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Copy/paste framework JSON or edit directly. Changes sync with the form above.
            </Typography>

            <TextField
                fullWidth
                multiline
                rows={12}
                value={jsonText}
                onChange={(e) => handleJsonChange(e.target.value)}
                disabled={disabled}
                error={!!jsonError}
                helperText={jsonError}
                InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                }}
                inputProps={{ 'aria-label': 'Framework JSON' }}
            />
        </Box>
    )
}

export default ManualFrameworkEditor
